/**
 * Proximity voice chat.
 *
 * Topology: a full WebRTC mesh, signalled through the Colyseus room. Six
 * players means at most five peer connections each — far below where a mesh
 * hurts, and it keeps voice latency at one network hop with no media server
 * to run or pay for. The game server relays only the SDP/ICE handshake.
 *
 * Rendering: each remote stream is fed into an AudioEngine voice channel — the
 * same HRTF-plus-air-absorption model as every other sound in the game — so a
 * voice is located by ear exactly the way a stomp is, and fades into the fog
 * the same way.
 *
 * Gameplay: the mic's measured level is reported to the server, which folds it
 * into the noise system. Talking is emitting. The monster does not distinguish
 * between a voice and a footstep, and this file does not either — it just
 * measures honestly and lets tuning decide what talking costs.
 *
 * Mesh maintenance is frame-driven rather than event-driven: every frame the
 * game hands over the current player list and this class converges toward it.
 * Dead connections get torn down and rebuilt on a cooldown, joins get offered
 * to, leavers get closed. No callback ordering to reason about.
 */

import {
  ClientMsg,
  VOICE,
  voiceNoise,
  clamp01,
  type IceServerConfig,
  type VoiceRelayMsg,
} from '@static-pines/shared';

import type { AudioEngine, VoiceChannel } from '../audio/AudioEngine.js';
import type { Connection } from './Connection.js';

/** Wire format inside the opaque signalling payload. */
type SignalPayload =
  | { kind: 'offer' | 'answer'; sdp: RTCSessionDescriptionInit }
  | { kind: 'ice'; candidate: RTCIceCandidateInit }
  /**
   * "My microphone arrived after our connection was built — please offer
   * again so it can be negotiated in." Sent by non-initiators only: the
   * glare-avoidance rule says they may never create offers themselves, so
   * they have to ask the peer who may.
   */
  | { kind: 'renegotiate' };

export type MicStatus = 'off' | 'starting' | 'live' | 'denied';

/** Seconds to wait before rebuilding a failed peer connection. Stored in ms. */
const RETRY_COOLDOWN = 3;

/** RMS at which the level meter reads 1.0. Speech with AGC sits well below. */
const RMS_FULL_SCALE = 0.25;

interface Peer {
  pc: RTCPeerConnection;
  /**
   * Chrome workaround: a remote MediaStream is silent through Web Audio
   * unless it is also attached to a media element. The element stays muted —
   * it exists purely to make the stream flow. Must stay referenced or GC
   * silences the peer minutes into a match.
   */
  audioEl: HTMLAudioElement | null;
  channel: VoiceChannel | null;
  analyser: AnalyserNode | null;
  /** Smoothed incoming level, for the speaking indicator. */
  level: number;
  speaking: boolean;
  /** ICE candidates that arrived before the remote description. */
  pendingIce: RTCIceCandidateInit[];
  initiator: boolean;
}

export class VoiceChat {
  status: MicStatus = 'off';
  /** User-controlled mute; independent of being dead, which also silences. */
  muted = false;
  /** True while the local mic is registering deliberate speech. */
  speaking = false;
  /** The noise the server currently attributes to this voice, for HUD warnings. */
  ownNoise = 0;

  private mic: MediaStream | null = null;
  private micAnalyser: AnalyserNode | null = null;
  private micLevel = 0;

  private peers = new Map<string, Peer>();
  /** Session ids on rebuild cooldown, mapped to the earliest retry time. */
  private retryAt = new Map<string, number>();
  /**
   * Teammates this player has chosen not to hear. Purely local — their audio
   * still arrives (the connection is untouched, so unmuting is instant) but
   * its channel gain is zero. Keyed by session id so the choice survives a
   * peer connection being rebuilt.
   */
  private mutedPeers = new Set<string>();

  private ice: IceServerConfig[] = [];
  private levelTimer = 0;
  private lastSentLevel = 0;
  private disposed = false;
  /**
   * Timer-driven mesh convergence, alongside the frame-driven one.
   *
   * requestAnimationFrame freezes in a backgrounded tab, so a host who
   * alt-tabs while friends join would never build their side of the mesh —
   * they would return to a silent lobby. Intervals keep (throttled) ticking
   * in background tabs, so connections still form; only positions and the
   * VAD wait for the tab to be visible again, and those are meaningless to
   * a player who is not looking anyway.
   */
  private meshTimer = 0;

  constructor(
    private readonly audio: AudioEngine,
    private readonly connection: Connection,
  ) {}

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  /**
   * Ask for the microphone and stand ready to build the mesh.
   *
   * Denial is not failure: a mic-less player still joins every connection
   * receive-only and hears everyone. They are a listener, not an outcast.
   */
  async start(ice: IceServerConfig[]): Promise<void> {
    this.ice = ice;
    this.status = 'starting';
    this.meshTimer = window.setInterval(() => this.convergeFromState(), 1000);

    try {
      this.mic = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      this.status = 'live';
    } catch {
      this.status = 'denied';
      return;
    }
    if (this.disposed) {
      // The match ended while the permission prompt was open.
      this.mic.getTracks().forEach((t) => t.stop());
      this.mic = null;
      return;
    }

    const ctx = this.audio.context;
    if (ctx && this.mic) {
      const source = ctx.createMediaStreamSource(this.mic);
      this.micAnalyser = ctx.createAnalyser();
      this.micAnalyser.fftSize = 1024;
      // Analyser only — the local mic must never reach the speakers.
      source.connect(this.micAnalyser);
    }

    this.attachMicToExistingPeers();
  }

  /**
   * Retrofit a late microphone onto connections built before it existed.
   *
   * getUserMedia is slow on phones — a permission prompt can sit on screen
   * for many seconds — while the mesh builds connections within a second of
   * joining. Tracks used to be attached only at construction, so a player
   * whose prompt outlived their first connection negotiated receive-only
   * *forever*: they heard everyone and were heard by no one. This is the
   * repair: add the track everywhere it is missing and renegotiate.
   */
  private attachMicToExistingPeers(): void {
    if (!this.mic) return;

    for (const [id, peer] of this.peers) {
      if (peer.pc.getSenders().some((sender) => sender.track)) continue;

      for (const track of this.mic.getAudioTracks()) peer.pc.addTrack(track, this.mic);

      // For initiators, addTrack fires onnegotiationneeded and the re-offer
      // happens by itself. Non-initiators must ask.
      if (!peer.initiator) this.sendSignal(id, { kind: 'renegotiate' });
    }
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    return this.muted;
  }

  dispose(): void {
    this.disposed = true;
    window.clearInterval(this.meshTimer);
    for (const id of [...this.peers.keys()]) this.teardown(id);
    this.mic?.getTracks().forEach((t) => t.stop());
    this.mic = null;
    this.micAnalyser = null;
    this.status = 'off';
  }

  /** Names of peers currently speaking, for the HUD. */
  speakers(names: Map<string, string>): string[] {
    const out: string[] = [];
    for (const [id, peer] of this.peers) {
      if (peer.speaking) out.push(names.get(id) ?? id);
    }
    return out;
  }

  /**
   * Whether a specific teammate is talking right now.
   *
   * Driven by the VAD on their *incoming stream*, not by network state — it
   * lights up exactly when you can hear them, which is the only definition of
   * "talking" that never disagrees with your ears. By the same principle, a
   * peer you have muted never reads as speaking: you cannot hear them.
   */
  isSpeaking(sessionId: string): boolean {
    if (this.mutedPeers.has(sessionId)) return false;
    return this.peers.get(sessionId)?.speaking ?? false;
  }

  /** Locally silence or restore a teammate. Affects nobody but this client. */
  togglePeerMute(sessionId: string): boolean {
    const muted = !this.mutedPeers.has(sessionId);
    if (muted) this.mutedPeers.add(sessionId);
    else this.mutedPeers.delete(sessionId);

    const channel = this.peers.get(sessionId)?.channel;
    if (channel) channel.input.gain.value = muted ? 0 : 1;
    return muted;
  }

  isPeerMuted(sessionId: string): boolean {
    return this.mutedPeers.has(sessionId);
  }

  // -------------------------------------------------------------------------
  // Per-frame convergence
  // -------------------------------------------------------------------------

  /**
   * @param now performance.now(), milliseconds — the game loop's clock
   * @param others every other player: session id and mouth-height position
   * @param selfAlive dead players hear but are never heard
   */
  update(
    dt: number,
    now: number,
    others: Array<{ id: string; x: number; y: number; z: number }>,
    selfAlive: boolean,
  ): void {
    if (this.disposed) return;

    this.converge(
      others.map((o) => o.id),
      now,
    );

    for (const other of others) {
      this.peers.get(other.id)?.channel?.setPosition(other.x, other.y, other.z);
    }

    // Outbound audio: your track is live only while you are audible-by-design.
    const sendable = this.status === 'live' && !this.muted && selfAlive;
    if (this.mic) {
      for (const track of this.mic.getAudioTracks()) track.enabled = sendable;
    }

    this.updateVad(dt, sendable);
    this.updateRemoteSpeaking(dt);
  }

  /** Make the peer set match the given session ids: build missing, drop gone. */
  private converge(ids: string[], now: number): void {
    const present = new Set(ids);

    for (const id of ids) {
      if (this.peers.has(id)) continue;
      const retry = this.retryAt.get(id) ?? 0;
      if (now >= retry) this.createPeer(id);
    }

    // Anyone no longer in the room drops out of the mesh.
    for (const id of [...this.peers.keys()]) {
      if (!present.has(id)) this.teardown(id);
    }
  }

  /** The background-tab path: read the roster straight from room state. */
  private convergeFromState(): void {
    if (this.disposed) return;
    const state = this.connection.state;
    if (!state) return;

    const ids: string[] = [];
    for (const id of state.players.keys()) {
      if (id !== this.connection.sessionId) ids.push(id);
    }
    this.converge(ids, performance.now());

    // Coarse positioning so a channel built while backgrounded is not left
    // sitting at the world origin; the frame loop refines it once visible.
    for (const [id, player] of state.players) {
      this.peers.get(id)?.channel?.setPosition(player.x, player.y + 1.6, player.z);
    }
  }

  /**
   * Voice activity detection on the local mic, and the level report.
   *
   * The report is the gameplay-critical part: it is what makes the monster
   * able to hear you talk. It is throttled to VOICE.SEND_HZ and levels expire
   * server-side, so silence costs no bandwidth at all.
   */
  private updateVad(dt: number, sendable: boolean): void {
    const raw = sendable ? this.readRms(this.micAnalyser) / RMS_FULL_SCALE : 0;
    const target = clamp01(raw);

    // Fast attack, slow release: the indicator snaps on with the first word
    // and lingers through the gaps between words instead of flickering.
    const tau = target > this.micLevel ? VOICE.SPEAK_ATTACK : VOICE.SPEAK_RELEASE;
    this.micLevel += ((target - this.micLevel) * dt) / Math.max(tau, dt);

    this.speaking = this.micLevel > VOICE.GATE;
    this.ownNoise = voiceNoise(this.micLevel);

    this.levelTimer -= dt;
    if (this.levelTimer > 0) return;
    this.levelTimer = 1 / VOICE.SEND_HZ;

    // Send while audible, plus one closing zero so the server-side floor
    // drops immediately rather than waiting out the TTL.
    const level = this.speaking ? this.micLevel : 0;
    if (level > 0 || this.lastSentLevel > 0) {
      this.connection.send(ClientMsg.VoiceLevel, { level });
      this.lastSentLevel = level;
    }
  }

  private updateRemoteSpeaking(dt: number): void {
    for (const peer of this.peers.values()) {
      const raw = clamp01(this.readRms(peer.analyser) / RMS_FULL_SCALE);
      const tau = raw > peer.level ? VOICE.SPEAK_ATTACK : VOICE.SPEAK_RELEASE;
      peer.level += ((raw - peer.level) * dt) / Math.max(tau, dt);
      peer.speaking = peer.level > VOICE.GATE;
    }
  }

  private readRms(analyser: AnalyserNode | null): number {
    if (!analyser) return 0;
    const data = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) sum += data[i]! * data[i]!;
    return Math.sqrt(sum / data.length);
  }

  // -------------------------------------------------------------------------
  // Mesh
  // -------------------------------------------------------------------------

  /**
   * The glare-avoidance rule for the whole mesh: the lexically smaller
   * session id makes the offer, always. Both sides compute it independently
   * and agree, so simultaneous offers are impossible by construction and no
   * rollback machinery is needed.
   */
  private createPeer(id: string): Peer {
    const initiator = this.connection.sessionId < id;
    const pc = new RTCPeerConnection({ iceServers: this.ice as RTCIceServer[] });

    const peer: Peer = {
      pc,
      audioEl: null,
      channel: null,
      analyser: null,
      level: 0,
      speaking: false,
      pendingIce: [],
      initiator,
    };
    this.peers.set(id, peer);

    if (this.mic) {
      for (const track of this.mic.getAudioTracks()) pc.addTrack(track, this.mic);
    } else if (initiator) {
      // No mic, but the offer still needs an audio section to receive into.
      pc.addTransceiver('audio', { direction: 'recvonly' });
    }

    pc.onicecandidate = (e) => {
      if (e.candidate) this.sendSignal(id, { kind: 'ice', candidate: e.candidate.toJSON() });
    };

    pc.ontrack = (e) => {
      const stream = e.streams[0] ?? new MediaStream([e.track]);
      this.attachStream(id, peer, stream);
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        // Tear down and let the frame loop rebuild after a cooldown —
        // self-healing without a reconnect state machine.
        this.teardown(id);
        this.retryAt.set(id, performance.now() + RETRY_COOLDOWN * 1000);
      }
    };

    if (initiator) {
      pc.onnegotiationneeded = async () => {
        try {
          await pc.setLocalDescription(await pc.createOffer());
          if (pc.localDescription) this.sendSignal(id, { kind: 'offer', sdp: pc.localDescription });
        } catch (error) {
          console.warn('[voice] offer failed', error);
        }
      };
    }

    return peer;
  }

  private attachStream(id: string, peer: Peer, stream: MediaStream): void {
    // Renegotiation re-fires ontrack for a stream that is already wired up;
    // attaching twice would double the voice and leak a channel.
    if (peer.channel) return;

    // The Chrome keep-alive element. Muted: playback happens in Web Audio.
    const el = new Audio();
    el.srcObject = stream;
    el.muted = true;
    void el.play().catch(() => undefined);
    peer.audioEl = el;

    const ctx = this.audio.context;
    if (!ctx) return;

    peer.channel = this.audio.createVoiceChannel();
    if (peer.channel) {
      ctx.createMediaStreamSource(stream).connect(peer.channel.input);
      // A locally-muted teammate stays muted across reconnects and late
      // stream arrivals — the choice is keyed to the player, not the wire.
      if (this.mutedPeers.has(id)) peer.channel.input.gain.value = 0;
    }

    peer.analyser = ctx.createAnalyser();
    peer.analyser.fftSize = 1024;
    ctx.createMediaStreamSource(stream).connect(peer.analyser);
  }

  /** Handle a relayed signal from another session. */
  async onSignal(msg: VoiceRelayMsg): Promise<void> {
    if (this.disposed) return;

    let payload: SignalPayload;
    try {
      payload = JSON.parse(msg.data) as SignalPayload;
    } catch {
      return;
    }

    // An offer can arrive before our frame loop has met this player — their
    // client saw us in state first. Meet them halfway.
    const peer = this.peers.get(msg.from) ?? this.createPeer(msg.from);
    const pc = peer.pc;

    try {
      switch (payload.kind) {
        case 'offer': {
          await pc.setRemoteDescription(payload.sdp);
          await this.drainIce(peer);
          await pc.setLocalDescription(await pc.createAnswer());
          if (pc.localDescription) {
            this.sendSignal(msg.from, { kind: 'answer', sdp: pc.localDescription });
          }
          break;
        }
        case 'answer': {
          await pc.setRemoteDescription(payload.sdp);
          await this.drainIce(peer);
          break;
        }
        case 'ice': {
          if (pc.remoteDescription) await pc.addIceCandidate(payload.candidate);
          else peer.pendingIce.push(payload.candidate);
          break;
        }
        case 'renegotiate': {
          // Only the initiator may act on this — anyone else offering would
          // reintroduce the glare the initiator rule exists to prevent.
          if (!peer.initiator) break;
          await pc.setLocalDescription(await pc.createOffer());
          if (pc.localDescription) {
            this.sendSignal(msg.from, { kind: 'offer', sdp: pc.localDescription });
          }
          break;
        }
      }
    } catch (error) {
      console.warn('[voice] signal handling failed', error);
    }
  }

  private async drainIce(peer: Peer): Promise<void> {
    const queued = peer.pendingIce.splice(0);
    for (const candidate of queued) {
      await peer.pc.addIceCandidate(candidate).catch(() => undefined);
    }
  }

  private sendSignal(to: string, payload: SignalPayload): void {
    this.connection.send(ClientMsg.VoiceSignal, { to, data: JSON.stringify(payload) });
  }

  private teardown(id: string): void {
    const peer = this.peers.get(id);
    if (!peer) return;
    this.peers.delete(id);

    peer.pc.onicecandidate = null;
    peer.pc.ontrack = null;
    peer.pc.onconnectionstatechange = null;
    peer.pc.onnegotiationneeded = null;
    peer.pc.close();
    peer.channel?.dispose();
    if (peer.audioEl) {
      peer.audioEl.srcObject = null;
      peer.audioEl = null;
    }
  }
}
